import React from 'react';
import { gql, useQuery } from '@apollo/client';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import CircularProgress from '@mui/material/CircularProgress';
import CloseIcon from '@mui/icons-material/Close';
import useResponsive from '../../../hooks/useResponsive';

const CATEGORIES_QUERY = gql`
  query CategoriesForDrilldown {
    categories {
      id
      name
      color
    }
  }
`;

const DRILLDOWN_QUERY = gql`
  query DrilldownTransactions($categoryIds: [Int], $dateFrom: Date, $dateTo: Date) {
    transactions(
      categoryIds: $categoryIds
      dateFrom: $dateFrom
      dateTo: $dateTo
      isHidden: false
      limit: 100
    ) {
      items {
        id
        date
        amount
        bankDescription
      }
      totalCount
    }
  }
`;

const formatCLP = (amount) => {
  const num = parseFloat(amount);
  const sign = num < 0 ? '-' : '+';
  const abs = Math.abs(num);
  return `${sign}$${abs.toLocaleString('es-CL', { minimumFractionDigits: 0 })}`;
};

const formatDate = (dateStr) => {
  const [year, month, day] = dateStr.split('-');
  return `${day}/${month}/${year}`;
};

const formatDateRange = (dateFrom, dateTo) => {
  if (!dateFrom && !dateTo) return 'Todo el tiempo';
  if (dateFrom && dateTo) return `${formatDate(dateFrom)} → ${formatDate(dateTo)}`;
  if (dateFrom) return `Desde ${formatDate(dateFrom)}`;
  return `Hasta ${formatDate(dateTo)}`;
};

export default function TransactionDrilldownModal({ open, onClose, categoryName, dateFrom, dateTo }) {
  const { isMobile } = useResponsive();
  const { data: catData } = useQuery(CATEGORIES_QUERY, { skip: !open });

  const categoryId = catData?.categories?.find((c) => c.name === categoryName)?.id ?? null;

  const { data, loading } = useQuery(DRILLDOWN_QUERY, {
    variables: {
      categoryIds: categoryId ? [parseInt(categoryId)] : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
    },
    skip: !open || !categoryId,
    fetchPolicy: 'cache-and-network',
  });

  const items = data?.transactions?.items ?? [];
  const totalCount = data?.transactions?.totalCount ?? 0;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle sx={{ pr: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 700 }}>
            {categoryName}
          </Typography>
          {!loading && totalCount > 0 && (
            <Box
              sx={{
                backgroundColor: 'secondary.main',
                color: 'primary.dark',
                borderRadius: 50,
                px: 1.25,
                py: 0.25,
                fontSize: '0.75rem',
                fontWeight: 700,
                lineHeight: 1.6,
              }}
            >
              {totalCount}
            </Box>
          )}
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          {formatDateRange(dateFrom, dateTo)}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', top: 12, right: 12 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 0, pb: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : items.length === 0 ? (
          <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
            Sin transacciones en este período
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Fecha</TableCell>
                <TableCell align="right">Monto</TableCell>
                <TableCell>Descripción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((tx) => {
                const num = parseFloat(tx.amount);
                const color = num < 0 ? '#D4710A' : '#16A34A';
                return (
                  <TableRow key={tx.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap', color: 'text.secondary', fontSize: '0.8rem' }}>
                      {formatDate(tx.date)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600, color, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
                      {formatCLP(tx.amount)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.82rem', maxWidth: { xs: 140, sm: 260 }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tx.bankDescription}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
        {!loading && totalCount > 100 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'right' }}>
            Mostrando 100 de {totalCount} transacciones
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
}
